import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Clock, Plus } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useEventTiming } from './EventTimingContext';
import { SortableTimingRow } from './SortableTimingRow';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { TEAMS_WARMUP_OPTIONS, TEAMS_TIME_OPTIONS } from './constants';

export default function TeamsTab() {
  const {
    isSuperUser,
    schedules,
    dialogOpen,
    setDialogOpen,
    editingTiming,
    setEditingTiming,
    saving,
    setSaving,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    deleting,
    handleDeleteClick,
    handleConfirmDelete,
    selectedScheduleId,
    setSelectedScheduleId,
    timingsLists,
    fetchTimings,
    sensors,
    handleDragEnd,
    canEditTiming,
    resetForm,
    shouldShowHR,
  } = useEventTiming();

  const [teamsDisciplines, setTeamsDisciplines] = useState([]);
  const [teamsTimings, setTeamsTimings] = useState({});

  const teamsTimingsList = timingsLists.teams || [];

  // Fetch Teams grades when schedule is selected
  useEffect(() => {
    if (!selectedScheduleId || editingTiming) return;

    const fetchTeamsGrades = async () => {
      try {
        const res = await axios.get(`${API}/event-timings/teams-grades/${selectedScheduleId}`, { withCredentials: true });
        const disciplines = res.data || [];
        setTeamsDisciplines(disciplines);
        
        const timings = {};
        disciplines.forEach(disc => {
          disc.grades.forEach(g => {
            timings[g.grade_id] = {
              warmup_per_team: '0:00',
              time_per_team: '7:00',
              can_shorten: false
            };
          });
        });
        setTeamsTimings(timings);
      } catch (error) {
        console.error('Error fetching teams grades:', error);
        toast.error('Failed to load grades for this schedule');
      }
    };
    fetchTeamsGrades();
  }, [selectedScheduleId, editingTiming]);

  const localResetForm = () => {
    resetForm();
    setTeamsDisciplines([]);
    setTeamsTimings({});
  };

  const handleAddTimings = () => {
    localResetForm();
    setDialogOpen(true);
  };

  const handleEditTiming = async (timing) => {
    setEditingTiming(timing);
    setSelectedScheduleId(timing.schedule_id);
    
    try {
      const res = await axios.get(`${API}/event-timings/teams-grades/${timing.schedule_id}`, { withCredentials: true });
      const disciplines = res.data || [];
      setTeamsDisciplines(disciplines);
      
      const savedTimingsMap = {};
      (timing.grade_timings || []).forEach(gt => {
        savedTimingsMap[gt.grade_id] = gt;
      });
      
      const timings = {};
      disciplines.forEach(disc => {
        disc.grades.forEach(g => {
          const saved = savedTimingsMap[g.grade_id];
          timings[g.grade_id] = {
            warmup_per_team: saved?.warmup_per_team || '0:00',
            time_per_team: saved?.time_per_team || '7:00',
            can_shorten: saved?.can_shorten || false
          };
        });
      });
      setTeamsTimings(timings);
    } catch (error) {
      console.error('Error fetching teams grades for edit:', error);
      toast.error('Failed to load timing data');
      return;
    }
    
    setDialogOpen(true);
  };

  const updateTeamsTiming = (gradeId, field, value) => {
    setTeamsTimings(prev => ({
      ...prev,
      [gradeId]: { ...prev[gradeId], [field]: value }
    }));
  };

  const handleSave = async () => {
    if (!selectedScheduleId) {
      toast.error('Please select a schedule');
      return;
    }

    const hasGrades = teamsDisciplines.some(d => d.grades.length > 0);
    if (!hasGrades) {
      toast.error('No teams grades found for this schedule');
      return;
    }

    setSaving(true);
    try {
      const allGradeTimings = [];
      teamsDisciplines.forEach(disc => {
        disc.grades.forEach(g => {
          const timing = teamsTimings[g.grade_id] || {};
          allGradeTimings.push({
            grade_id: g.grade_id,
            discipline: disc.discipline,
            warmup_per_team: timing.warmup_per_team || '0:00',
            time_per_team: timing.time_per_team || '7:00',
            can_shorten: timing.can_shorten || false
          });
        });
      });

      const payload = {
        schedule_id: selectedScheduleId,
        event_type: 'teams',
        grade_timings: allGradeTimings
      };

      await axios.post(`${API}/event-timings`, payload, { withCredentials: true });
      
      toast.success(editingTiming ? 'Teams timings updated successfully' : 'Teams timings saved successfully');
      setDialogOpen(false);
      localResetForm();
      fetchTimings('teams');
    } catch (error) {
      console.error('Error saving timings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save timings');
    } finally {
      setSaving(false);
    }
  };

  const renderTeamsTable = (discipline, grades) => (
    <div className="mb-6">
      <h3 className="font-medium mb-2">{discipline}</h3>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-green-100">
            <th className="border px-3 py-2 text-left" style={{ width: 'calc(100% - 360px)' }}>Grade</th>
            <th className="border px-3 py-2 text-center" style={{ width: '120px' }}>Warm up per Team</th>
            <th className="border px-3 py-2 text-center" style={{ width: '120px' }}>Time per Team</th>
            <th className="border px-3 py-2 text-center" style={{ width: '120px' }}>Can Shorten</th>
          </tr>
        </thead>
        <tbody>
          {grades.map((grade, index) => {
            const timing = teamsTimings[grade.grade_id] || {};
            const showHR = shouldShowHR(grades, index);
            
            return (
              <React.Fragment key={grade.grade_id}>
                {showHR && (
                  <tr>
                    <td colSpan={4} style={{ border: 'none', padding: '16px 0', background: 'white' }}>
                      <div style={{ height: '2px', backgroundColor: '#a8a29e' }}></div>
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="border px-3 py-2 bg-white font-medium">{grade.name}</td>
                  <td className="border px-1 py-1 bg-white">
                    <Select 
                      value={timing.warmup_per_team || '0:00'} 
                      onValueChange={(v) => updateTeamsTiming(grade.grade_id, 'warmup_per_team', v)}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                        {TEAMS_WARMUP_OPTIONS.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="border px-1 py-1 bg-white">
                    <Select 
                      value={timing.time_per_team || '7:00'} 
                      onValueChange={(v) => updateTeamsTiming(grade.grade_id, 'time_per_team', v)}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                        {TEAMS_TIME_OPTIONS.map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="border px-3 py-2 bg-white text-center">
                    <Checkbox
                      checked={timing.can_shorten || false}
                      onCheckedChange={(checked) => updateTeamsTiming(grade.grade_id, 'can_shorten', checked)}
                    />
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Teams</h2>
        <Button onClick={handleAddTimings} className="btn-primary" data-testid="add-timings-teams">
          <Plus className="mr-2 h-4 w-4" />
          Add Timings
        </Button>
      </div>
      
      {!isSuperUser && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          You can create and edit timing tables for schedules you own. Timing tables for system schedules are view-only.
        </div>
      )}
      
      <Card className="p-6">
        {teamsTimingsList.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 text-stone-300" />
            <p>No timing entries yet for Teams.</p>
            <p className="text-sm mt-1">Click "Add Timings" to create your first entry.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'teams')}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="w-8"></th>
                    <th className="text-left py-3 px-2 font-semibold">Schedule</th>
                    <th className="text-center py-3 px-2 font-semibold">Grades Configured</th>
                    <th className="text-center py-3 px-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <SortableContext items={teamsTimingsList.map(t => t.timing_id)} strategy={verticalListSortingStrategy}>
                    {teamsTimingsList.map(timing => (
                      <SortableTimingRow
                        key={timing.timing_id}
                        id={timing.timing_id}
                        timing={timing}
                        canEdit={canEditTiming(timing)}
                        canReorder={isSuperUser}
                        onEdit={handleEditTiming}
                        onDelete={handleDeleteClick}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </div>
          </DndContext>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(val) => { setDialogOpen(val); if (!val) localResetForm(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTiming ? 'Edit Teams Timings' : 'Add Teams Timings'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <Label>Select Schedule *</Label>
              <Select 
                value={selectedScheduleId} 
                onValueChange={setSelectedScheduleId}
                disabled={!!editingTiming}
              >
                <SelectTrigger data-testid="schedule-select">
                  <SelectValue placeholder="Choose a schedule..." />
                </SelectTrigger>
                <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                  {schedules.map(s => (
                    <SelectItem key={s.schedule_id} value={s.schedule_id}>
                      {s.schedule_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingTiming && (
                <p className="text-xs text-muted-foreground mt-1">Schedule cannot be changed when editing</p>
              )}
            </div>

            {teamsDisciplines.length > 0 && (
              <div className="space-y-6">
                {teamsDisciplines.map(disc => (
                  <div key={disc.discipline_id} className="overflow-x-auto">
                    {renderTeamsTable(disc.discipline, disc.grades)}
                  </div>
                ))}
              </div>
            )}

            {selectedScheduleId && teamsDisciplines.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No Teams disciplines found for this schedule.</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { setDialogOpen(false); localResetForm(); }}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="btn-primary"
                data-testid="save-teams-timings"
                disabled={!selectedScheduleId || teamsDisciplines.length === 0 || saving}
              >
                {saving ? 'Saving...' : (editingTiming ? 'Update Timings' : 'Save Timings')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemName={itemToDelete?.schedule_name ? `timing for "${itemToDelete.schedule_name}"` : 'this timing'}
        loading={deleting}
      />
    </>
  );
}
